import React, { useState } from 'react';
import {
    Box,
    Paper,
    Typography,
    Button,
    Alert,
    CircularProgress,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Chip,
    LinearProgress,
    Tooltip
} from '@mui/material';
import {
    Speed as SpeedIcon,
    CheckCircle as CheckIcon,
    Warning as WarningIcon,
    Error as ErrorIcon
} from '@mui/icons-material';
import { systemAPI } from '../../../services/systemAPI';

const ScrapingSpeedTest = ({ onConfigChange }) => {
    const [loading, setLoading] = useState(false);
    const [results, setResults] = useState(null);
    const [error, setError] = useState(null);

    const handleRunTest = async () => {
        try {
            setLoading(true);
            setError(null);
            setResults(null);

            const response = await systemAPI.runScrapingSpeedTest([1, 2]);

            if (response.success) {
                setResults(response.data);
            } else {
                setError(response.message || 'Erro ao executar teste');
            }
        } catch (err) {
            setError(err.response?.data?.message || err.message || 'Erro ao executar teste de velocidade');
        } finally {
            setLoading(false);
        }
    };

    const getStatusIcon = (successRate) => {
        if (successRate === 100) return <CheckIcon color="success" />;
        if (successRate >= 66) return <WarningIcon color="warning" />;
        return <ErrorIcon color="error" />;
    };

    const getSpeedupColor = (speedup) => {
        if (speedup >= 1.5) return 'success';
        if (speedup >= 1.2) return 'info';
        return 'default';
    };

    return (
        <Paper sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <SpeedIcon color="primary" />
                <Typography variant="h6">
                    Teste de Velocidade de Scraping
                </Typography>
            </Box>

            <Alert severity="info" sx={{ mb: 3 }}>
                Este teste executa scraping de 3 sites de exemplo com diferentes níveis de paralelismo (1 e 2) 
                para comparar a velocidade. O teste não altera a configuração atual do sistema.
            </Alert>

            {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                    {error}
                </Alert>
            )}

            <Button
                variant="contained"
                color="primary"
                onClick={handleRunTest}
                disabled={loading}
                startIcon={loading ? <CircularProgress size={20} /> : <SpeedIcon />}
                sx={{ mb: 3 }}
            >
                {loading ? 'Executando Teste...' : '▶️ Iniciar Teste de Velocidade'}
            </Button>

            {loading && (
                <Box sx={{ mb: 2 }}>
                    <LinearProgress />
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
                        Processando {results ? results.results.length : 2} níveis de paralelismo...
                    </Typography>
                </Box>
            )}

            {results && (
                <Box>
                    <TableContainer>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell><strong>Paralelismo</strong></TableCell>
                                    <TableCell><strong>Tempo Total</strong></TableCell>
                                    <TableCell><strong>Média/URL</strong></TableCell>
                                    <TableCell><strong>Sucesso</strong></TableCell>
                                    <TableCell><strong>Speedup</strong></TableCell>
                                    <TableCell><strong>Status</strong></TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {results.results.map((result) => (
                                    <TableRow
                                        key={result.level}
                                        sx={{
                                            bgcolor: result.level === results.bestLevel 
                                                ? 'action.selected' 
                                                : 'inherit'
                                        }}
                                    >
                                        <TableCell>
                                            <Typography variant="body2">
                                                <strong>{result.level}</strong> URL{result.level > 1 ? 's' : ''}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            {(result.durationMs / 1000).toFixed(1)}s
                                        </TableCell>
                                        <TableCell>
                                            {(result.avgPerUrl / 1000).toFixed(1)}s
                                        </TableCell>
                                        <TableCell>
                                            <Chip
                                                size="small"
                                                label={`${result.successCount}/3`}
                                                color={result.successCount === 3 ? 'success' : 'warning'}
                                                variant="outlined"
                                            />
                                        </TableCell>
                                        <TableCell>
                                            {result.speedup ? (
                                                <Chip
                                                    size="small"
                                                    label={result.speedupFormatted}
                                                    color={getSpeedupColor(result.speedup)}
                                                />
                                            ) : (
                                                <Typography variant="body2" color="text.secondary">
                                                    baseline
                                                </Typography>
                                            )}
                                        </TableCell>
                                        <TableCell>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                {getStatusIcon(result.successRate)}
                                                <Typography variant="caption">
                                                    {result.successRate}%
                                                </Typography>
                                            </Box>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>

                    {results.bestLevel && (
                        <Alert 
                            severity="success" 
                            sx={{ mt: 3 }}
                            action={
                                <Button 
                                    color="inherit" 
                                    size="small"
                                    onClick={() => onConfigChange?.(results.bestLevel)}
                                >
                                    Usar {results.bestLevel === 1 ? 'Sequencial' : 'Paralelo'}
                                </Button>
                            }
                        >
                            <Typography variant="body2">
                                <strong>Recomendação:</strong> Paralelismo {results.bestLevel === 1 ? 'Sequencial (1 URL)' : 'Paralelo (2 URLs)'}
                                {' '}resultou em {results.bestSpeedup} de velocidade.
                            </Typography>
                        </Alert>
                    )}

                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2 }}>
                        Tempo total do teste: {(results.totalDuration / 1000).toFixed(1)}s
                        {' · '}
                        Sites testados: g1.globo.com, uol.com.br, r7.com
                    </Typography>
                </Box>
            )}
        </Paper>
    );
};

export default ScrapingSpeedTest;
